import { supabase } from '@/integrations/supabase/client';
import { getAuthUser } from '@/lib/auth-bridge';
import { sanitizeStringForLog, safeError } from '@/lib/safeLog';
import { captureSentryException } from '@/lib/sentry';

type ErrorType = 'react_boundary' | 'unhandled_exception' | 'promise_rejection' | 'api_error';
type Severity = 'error' | 'warning' | 'critical';

/**
 * Sanitize a value for safe logging to prevent log injection (CWE-117).
 * Removes/encodes newlines, carriage returns, and other control characters
 * that could be used to forge log entries or break log integrity.
 */
export function sanitizeForLog(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }

  let stringValue: string;
  
  if (value instanceof Error) {
    // Extract safe error message and code if available
    const errorWithCode = value as Error & { code?: string; hint?: string };
    stringValue = errorWithCode.code 
      ? `[${errorWithCode.code}] ${value.message}`
      : value.message;
  } else if (typeof value === 'object') {
    try {
      stringValue = JSON.stringify(value);
    } catch {
      stringValue = '[Unserializable object]';
    }
  } else {
    stringValue = String(value);
  }

  // Remove control characters that could be used for log injection:
  // - Newlines (\n, \r) - prevent log forging via multi-line injection
  // - Carriage returns - prevent line overwriting
  // - Other control chars (ASCII 0-31 except tab, which we replace with space)
  // eslint-disable-next-line no-control-regex
  const controlCharsRegex = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
  return stringValue
    .replace(/[\r\n]/g, ' ')  // Replace newlines with space
    .replace(controlCharsRegex, ''); // Remove other control chars
}

/**
 * Safe console.error wrapper that sanitizes input to prevent log injection.
 * Use this instead of console.error when logging potentially untrusted data.
 */
export function safeLogError(message: string, error?: unknown): void {
  const sanitizedMessage = sanitizeForLog(message);
  if (error !== undefined) {
    console.error(sanitizedMessage, sanitizeForLog(error));
  } else {
    console.error(sanitizedMessage);
  }
}

interface ErrorLogData {
  error: Error;
  errorType: ErrorType;
  componentStack?: string;
  metadata?: Record<string, unknown>;
  severity?: Severity;
}

/**
 * Log an error to the Supabase error_logs table.
 * Fails silently to avoid causing additional errors.
 */
export async function logError(data: ErrorLogData): Promise<void> {
  try {
    captureSentryException(data.error, {
      errorType: data.errorType,
      severity: data.severity,
      componentStack: data.componentStack,
      metadata: data.metadata,
    });
  } catch (e) {
    safeError('Failed to log error to Sentry:', e);
  }

  try {
    // Get current user if authenticated (don't block on this)
    const user = await getAuthUser();
    
    await (supabase.from('error_logs') as any).insert({
      user_id: user?.id || null,
      error_message: sanitizeStringForLog(data.error.message) || 'Unknown error',
      error_stack: sanitizeStringForLog(data.error.stack) || null,
      component_stack: sanitizeStringForLog(data.componentStack) || null,
      error_type: data.errorType,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      metadata: data.metadata || {},
      severity: data.severity || 'error',
    });
  } catch (e) {
    // Fail silently - don't let error logging cause more errors
    safeError('Failed to log error to Supabase:', e);
  }
}

/**
 * Setup global error handlers for unhandled exceptions and promise rejections.
 * Call this once at app initialization.
 */
export function setupGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  // Unhandled JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      logError({
        error,
        errorType: 'unhandled_exception',
        metadata: { source, lineno, colno, message: String(message) },
        severity: 'critical',
      });
    }
    // Return false to allow default browser error handling
    return false;
  };

  // Unhandled Promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    logError({
      error,
      errorType: 'promise_rejection',
      severity: 'error',
    });
  };
}
